import React, { useEffect, useState } from 'react';
import { ArrowRight } from 'lucide-react';
import { useModal } from '../context/ModalContext';
import useScrollAnimation from '../hooks/useScrollAnimation';
import { DottedSurface } from './ui/dotted-surface';
import MetricsBar from './MetricsBar';

const rotatingVerbs = ['Operate', 'Automate', 'Deliver', 'Scale', 'Grow'];

const Hero = () => {
  const { openDemoModal } = useModal();
  const [heroRef, heroVisible] = useScrollAnimation({ threshold: 0.1 });
  const [activeVerbIndex, setActiveVerbIndex] = useState(0);

  useEffect(() => {
    const cycleTimer = window.setInterval(() => {
      setActiveVerbIndex((current) => (current + 1) % rotatingVerbs.length);
    }, 1900);

    return () => window.clearInterval(cycleTimer);
  }, []);

  return (
    <section className="relative pt-16 md:pt-20 bg-[--bg-primary] overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[76vh] pointer-events-none overflow-hidden">
        <div className="absolute inset-0 hero-ambient" />
        <DottedSurface className="opacity-80 [mask-image:linear-gradient(to_bottom,black_8%,black_68%,transparent_100%)]" />
        <div className="hero-sweep" />
        <div className="hidden sm:block hero-orb hero-orb--one w-[28rem] h-[28rem] top-[-8rem] right-[-7rem]" />
        <div className="hidden sm:block hero-orb hero-orb--two w-[20rem] h-[20rem] top-[38%] left-[-5rem]" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-[--bg-primary]" />
      </div>
      
      <div className="relative z-10 max-w-[1500px] mx-auto px-4 sm:px-6 pt-16 pb-6 lg:pt-20 lg:pb-8">
        <div ref={heroRef} className="grid lg:grid-cols-[1.12fr_0.88fr] gap-12 xl:gap-20 items-center">
          {/* Left Column - Text Content */}
          <div>
            <p
              className={`section-eyebrow mb-6 transition-all duration-700 ease-out ${
                heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'
              }`}
            >
              AI Transformation Studio
            </p>
            <h1 className="display-title font-semibold text-[--text-primary] overflow-hidden">
              <span 
                className={`block transition-all duration-700 ease-out ${
                  heroVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-full opacity-0'
                }`}
              >
                Change the way you
              </span>
              <span 
                className={`block accent-wash transition-all duration-700 ease-out delay-150 ${
                  heroVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-full opacity-0'
                }`}
              >
                <span className="relative inline-flex min-w-[7ch] sm:min-w-[9ch] h-[1.05em] align-top overflow-hidden">
                  {rotatingVerbs.map((verb, index) => (
                    <span
                      key={verb}
                      className={`absolute left-0 top-0 whitespace-nowrap transition-all duration-500 ease-out ${
                        activeVerbIndex === index
                          ? 'translate-y-0 opacity-100'
                          : 'translate-y-3 opacity-0'
                      }`}
                    >
                      {verb}
                    </span>
                  ))}
                </span>
              </span>
              <span 
                className={`block transition-all duration-700 ease-out delay-300 ${
                  heroVisible 
                    ? 'translate-y-0 opacity-100' 
                    : 'translate-y-full opacity-0'
                }`}
              >
                with Synentia
              </span>
            </h1>
            
            <p 
              className={`mt-7 text-[1.05rem] text-[--text-secondary] max-w-2xl lg:max-w-xl leading-relaxed transition-all duration-700 ease-out delay-500 ${
                heroVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-8 opacity-0'
              }`}
            >
              From roadmap to rollout, we design, ship, and scale production-ready AI across
              customer operations, internal workflows, and product experiences.
            </p>

            {/* CTA Buttons */}
            <div 
              className={`mt-10 flex flex-wrap gap-4 transition-all duration-700 ease-out delay-700 ${
                heroVisible 
                  ? 'translate-y-0 opacity-100' 
                  : 'translate-y-8 opacity-0'
              }`}
            >
              <button
                onClick={openDemoModal}
                className="px-7 py-3.5 text-[11px] font-medium text-[--bg-primary] uppercase tracking-[0.18em] font-mono bg-[--accent] rounded-full hover:bg-[--accent-hover] transition-all flex items-center gap-2 btn-pattern"
              >
                Book a Free Audit
                <ArrowRight className="w-4 h-4" />
              </button>
              <a 
                href="#how-it-works"
                className="px-7 py-3.5 text-[11px] font-medium text-[--text-primary] uppercase tracking-[0.18em] font-mono bg-transparent border border-[--border] rounded-full hover:border-[--border-hover] hover:bg-[--bg-secondary] transition-colors"
              >
                How It Works
              </a>
            </div>
          </div>

          {/* Right Column - Terminal/Code Block */}
          <div 
            className={`hidden lg:block transition-all duration-1000 ease-out delay-300 ${
              heroVisible 
                ? 'translate-x-0 opacity-100' 
                : 'translate-x-12 opacity-0'
            }`}
          >
            <div className="soft-card rounded-2xl p-7 font-mono">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-3 h-3 rounded-full bg-[#ff5f57]" />
                <div className="w-3 h-3 rounded-full bg-[#febc2e]" />
                <div className="w-3 h-3 rounded-full bg-[#28c840]" />
              </div>
              <div className="space-y-3 text-sm">
                <div className="text-[--text-muted]">// synentia/runbook</div>
                <div>
                  <span className="text-[--accent]">const</span>{' '}
                  <span className="text-[--text-primary]">playbook</span>{' '}
                  <span className="text-[--text-muted]">=</span>{' '}
                  <span className="text-[--accent]">await</span>{' '}
                  <span className="text-[--text-primary]">synentia</span>
                  <span className="text-[--text-muted]">.</span>
                  <span className="text-[--text-primary]">deploy</span>
                  <span className="text-[--text-muted]">(</span>
                  <span className="text-[#98c379]">{'{'}</span>
                </div>
                <div className="pl-4">
                  <span className="text-[--text-primary]">workflow</span>
                  <span className="text-[--text-muted]">:</span>{' '}
                  <span className="text-[#98c379]">"support-ops"</span>
                  <span className="text-[--text-muted]">,</span>
                </div>
                <div className="pl-4">
                  <span className="text-[--text-primary]">integration</span>
                  <span className="text-[--text-muted]">:</span>{' '}
                  <span className="text-[#98c379]">"crm + helpdesk"</span>
                  <span className="text-[--text-muted]">,</span>
                </div>
                <div className="pl-4">
                  <span className="text-[--text-primary]">target</span>
                  <span className="text-[--text-muted]">:</span>{' '}
                  <span className="text-[#98c379]">"4 week launch"</span>
                </div>
                <div>
                  <span className="text-[#98c379]">{'}'}</span>
                  <span className="text-[--text-muted]">)</span>
                  <span className="text-[--text-muted]">;</span>
                </div>
                <div className="mt-4 pt-4 border-t border-[--border]">
                  <span className="text-[#28c840]">✓</span>{' '}
                  <span className="text-[--text-secondary]">Agent flow deployed to production</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <MetricsBar />

      </div>
    </section>
  );
};

export default Hero;
